import { QueryClient } from "@tanstack/react-query"
import { httpBatchLink } from "@trpc/client"
import { fetchAuthSession } from "aws-amplify/auth"
import { Localisation } from "i18n/Localisation"
import { getInventIframeToken, isInventEmbedPathname } from "lib/invent-iframe-bridge"
import { getInventPortalOrigin } from "lib/invent-widget-runtime"
import { retryCondition, trpc } from "lib/trpc"
import superjson from "superjson"
import type { RootContext } from "./RootContext"
import "lib/cognito"

const createTrpcClient = () => {
  if (!import.meta.env.VITE_BACKEND_URL) {
    throw new Error("Missing backend URL")
  }
  return trpc.createClient({
    links: [
      httpBatchLink({
        url: import.meta.env.VITE_BACKEND_URL,
        transformer: superjson,
        headers: async () => {
          if (isInventEmbedPathname(window.location.pathname)) {
            const parentOrigin = await getInventPortalOrigin()
            const token = await getInventIframeToken({
              parentOrigin,
            })
            return { authorization: `Bearer ${token}` }
          }

          const localUserEmail = import.meta.env.VITE_LOCAL_USER_EMAIL
          if (localUserEmail) {
            return {
              authorization: `Bearer local-${localUserEmail}`,
            }
          }

          try {
            const session = await fetchAuthSession()
            const accessToken = session.tokens?.accessToken?.toString()

            return {
              ...(accessToken && { authorization: `Bearer ${accessToken}` }),
            }
          } catch (error) {
            // biome-ignore lint/suspicious/noConsole: temporary for development
            console.error("Error: ", error)
            // User is not authenticated, return empty headers
            return {}
          }
        },
      }),
    ],
  })
}

export const getClientRootContext = (): RootContext => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: retryCondition,
      },
    },
  })

  return {
    i18n: Localisation.init("en-US"),
    queryClient,
    trpcClient: createTrpcClient(),
  }
}
